import type { BattleMode, BoxSummary } from "@loot-os/api-contract";
import {
  BATTLE_PLAYER_COUNTS,
  capitalizeTerm,
  formatAmountWithUnit,
  MAX_BATTLE_ROUNDS,
} from "@loot-os/api-contract";
import { isLootApiErrorLike, useClientSeed } from "@loot-os/react";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Label } from "@loot-os/ui/components/label";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { Switch } from "@loot-os/ui/components/switch";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { ErrorBanner } from "../components/ErrorBanner.js";
import { RequireAuth } from "../components/RequireAuth.js";
import { spendableCents } from "../lib/balance.js";
import { BATTLE_MODE_LABEL, BATTLE_MODE_RULE } from "../lib/battleModes.js";
import { useCreateBattle } from "../lib/battles.js";
import { battlePriceCents, expandRounds, initialBattlePicks } from "../lib/battleTimeline.js";
import { useCurrency } from "../lib/currency.js";
import { boxesQueryOptions } from "../lib/queries.js";
import { pendingRevealFromError, useRevealClaim } from "../lib/revealClaim.js";
import { useBalance } from "../lib/shopper.js";
import { useTenant } from "../lib/tenant.js";
import { useTerminology } from "../lib/terminology.js";

export function BattleCreatePage() {
  return (
    <RequireAuth>
      <CreateBattle />
    </RequireAuth>
  );
}

/**
 * Loads the boxes before the form mounts: the form's first picks come from them (`?boxId=`, the
 * box page's Battle link), so it starts once the list is known. A changed `boxId` remounts it.
 */
function CreateBattle() {
  const { box } = useTerminology();
  const boxes = useQuery(boxesQueryOptions());
  const { boxId } = useSearch({ from: "/battles/create" });

  if (boxes.isError) {
    return (
      <div className="grid gap-6">
        <CreateBattleHeading />
        <ErrorBanner>
          {boxes.error instanceof Error ? boxes.error.message : `failed to load ${box.plural}`}
        </ErrorBanner>
      </div>
    );
  }
  if (boxes.isPending) {
    return (
      <div className="grid gap-6">
        <CreateBattleHeading />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {["a", "b", "c"].map((id) => (
            <Skeleton key={id} className="h-40 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }
  return (
    <CreateBattleForm
      key={boxId ?? ""}
      boxes={boxes.data}
      initialBoxId={boxId}
      refetchBoxes={() => void boxes.refetch()}
    />
  );
}

function CreateBattleHeading() {
  const { box, battle } = useTerminology();
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">New {battle.singular}</h1>
      <p className="text-sm text-muted-foreground">
        Pick the {box.plural} and how many times each opens. Every seat pays the same entry and
        opens the same rounds.
      </p>
    </div>
  );
}

/** POST /v1/battles: pick boxes (quantity = rounds of that box), seats, mode, switches. */
function CreateBattleForm({
  boxes: boxList,
  initialBoxId,
  refetchBoxes,
}: {
  boxes: BoxSummary[];
  /** The box page's `?boxId=`: the picks start with one round of it. */
  initialBoxId: string | undefined;
  /** After a `battle_price_mismatch`: the list the entry was priced from is stale. */
  refetchBoxes: () => void;
}) {
  const currency = useCurrency();
  const unit = currency.namePlural;
  const { hasFeature } = useTenant();
  const terms = useTerminology();
  const navigate = useNavigate();
  const { promptClaim } = useRevealClaim();
  const balance = useBalance().data ?? null;
  const [clientSeed] = useClientSeed({});

  const [initialPicks] = useState(() => initialBattlePicks(boxList, initialBoxId));
  const [quantities, setQuantities] = useState<Record<string, number>>(initialPicks.quantities);
  const [order, setOrder] = useState<string[]>(initialPicks.order);
  const [playerCount, setPlayerCount] = useState<(typeof BATTLE_PLAYER_COUNTS)[number]>(2);
  const [mode, setMode] = useState<BattleMode>("standard");
  const [isPublic, setIsPublic] = useState(true);
  const [fastSpin, setFastSpin] = useState(false);
  const [startWithBots, setStartWithBots] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateBattle();
  const boxIds = useMemo(() => expandRounds(order, quantities), [order, quantities]);
  const priceCents = battlePriceCents(boxList, quantities);
  const spendable = balance ? spendableCents(balance) : null;
  const short = spendable !== null && spendable < priceCents;
  const roundsLeft = MAX_BATTLE_ROUNDS - boxIds.length;

  function setQuantity(box: BoxSummary, next: number) {
    const clamped = Math.max(0, Math.min(next, (quantities[box.id] ?? 0) + roundsLeft));
    setQuantities((prev) => ({ ...prev, [box.id]: clamped }));
    setOrder((prev) => {
      if (clamped === 0) return prev.filter((id) => id !== box.id);
      return prev.includes(box.id) ? prev : [...prev, box.id];
    });
  }

  function move(boxId: string, direction: -1 | 1) {
    setOrder((prev) => {
      const index = prev.indexOf(boxId);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });
  }

  return (
    <div className="grid gap-6">
      <CreateBattleHeading />

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Boxes</CardTitle>
              <CardDescription>
                Quantity is how many rounds of that box. Up to {MAX_BATTLE_ROUNDS} rounds in all.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {boxList.map((box) => {
                  const quantity = quantities[box.id] ?? 0;
                  return (
                    <div
                      key={box.id}
                      className={`grid gap-2 rounded-xl border p-3 ${
                        quantity > 0 ? "border-primary" : "border-border"
                      }`}
                    >
                      <div className="aspect-square overflow-hidden rounded-lg bg-muted">
                        {box.imageUrl ? (
                          <img src={box.imageUrl} alt="" className="size-full object-cover" />
                        ) : null}
                      </div>
                      <div className="grid leading-tight">
                        <span className="truncate text-sm font-medium">{box.name}</span>
                        <span className="font-mono text-xs text-muted-foreground">
                          {formatAmountWithUnit(box.priceCents, currency)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={quantity === 0}
                          onClick={() => setQuantity(box, quantity - 1)}
                          aria-label={`One fewer round of ${box.name}`}
                        >
                          −
                        </Button>
                        <span className="font-mono text-sm tabular-nums">{quantity}</span>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={roundsLeft <= 0}
                          onClick={() => setQuantity(box, quantity + 1)}
                          aria-label={`One more round of ${box.name}`}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {order.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Round order</CardTitle>
                <CardDescription>
                  {capitalizeTerm(terms.box.plural)} open in this order, each for its quantity of
                  rounds.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-2">
                {order.map((boxId, index) => {
                  const box = boxList.find((b) => b.id === boxId);
                  return (
                    <div
                      key={boxId}
                      className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
                    >
                      <span>
                        <span className="text-muted-foreground">{index + 1}.</span> {box?.name}{" "}
                        <span className="text-muted-foreground">× {quantities[boxId] ?? 0}</span>
                      </span>
                      <span className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={index === 0}
                          onClick={() => move(boxId, -1)}
                        >
                          ↑
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={index === order.length - 1}
                          onClick={() => move(boxId, 1)}
                        >
                          ↓
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => box && setQuantity(box, 0)}
                        >
                          Remove
                        </Button>
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : null}
        </div>

        <div className="grid content-start gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Settings</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid gap-1.5">
                <Label>Players</Label>
                <div className="flex gap-1.5">
                  {BATTLE_PLAYER_COUNTS.map((count) => (
                    <Button
                      key={count}
                      size="sm"
                      variant={playerCount === count ? "default" : "secondary"}
                      onClick={() => setPlayerCount(count)}
                    >
                      {count}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label>Mode</Label>
                {(["standard", "crazy"] as const).map((option) => (
                  <label
                    key={option}
                    className={`flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm ${
                      mode === option ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <input
                      type="radio"
                      name="mode"
                      className="mt-1"
                      checked={mode === option}
                      onChange={() => setMode(option)}
                    />
                    <span className="grid">
                      <span className="font-medium">{BATTLE_MODE_LABEL[option]}</span>
                      <span className="text-xs text-muted-foreground">
                        {BATTLE_MODE_RULE[option]}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <SwitchRow
                id="private"
                label="Private"
                hint="Only people with the link can join; other shoppers never see it in the lobby."
                checked={!isPublic}
                onChange={(v) => setIsPublic(!v)}
              />
              <SwitchRow
                id="fast"
                label="Fast spin"
                hint="Shorter rounds."
                checked={fastSpin}
                onChange={setFastSpin}
              />
              <SwitchRow
                id="bots"
                label="Start with bots"
                hint="Fill every other seat with a bot and start at once."
                checked={startWithBots}
                onChange={setStartWithBots}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Entry</CardTitle>
              <CardDescription>
                {boxIds.length} {boxIds.length === 1 ? "round" : "rounds"} · {playerCount} seats
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3">
              <p className="text-3xl font-semibold tracking-tight">
                {formatAmountWithUnit(priceCents, currency)}
              </p>
              {spendable !== null ? (
                <p className="text-xs text-muted-foreground">
                  Spendable: {formatAmountWithUnit(spendable, currency)}
                  {balance && balance.heldCents > 0
                    ? ` (${formatAmountWithUnit(balance.heldCents, currency)} held for an unclaimed reveal)`
                    : ""}
                </p>
              ) : null}
              {short ? (
                <p className="text-xs text-destructive">
                  Not enough {unit}.{" "}
                  {hasFeature("balancePurchases") ? (
                    <Link to="/account" className="underline">
                      Buy {unit}
                    </Link>
                  ) : null}
                </p>
              ) : null}
              <Button
                size="lg"
                disabled={boxIds.length === 0 || short || create.isPending}
                onClick={() => {
                  setError(null);
                  create.mutate(
                    {
                      boxIds,
                      playerCount,
                      mode,
                      isPublic,
                      fastSpin,
                      startWithBots,
                      clientSeed,
                      expectedPriceCents: priceCents,
                    },
                    {
                      onSuccess: (res) =>
                        void navigate({
                          to: "/battles/$battleId",
                          params: { battleId: res.battle.id },
                        }),
                      onError: (err) => {
                        if (isLootApiErrorLike(err) && err.code === "battle_price_mismatch") {
                          refetchBoxes();
                          setError("Prices changed — review the entry and try again.");
                        } else if (isLootApiErrorLike(err) && err.code === "reveal_pending") {
                          setError(null);
                          promptClaim(pendingRevealFromError(err));
                        } else {
                          setError(
                            err instanceof Error
                              ? err.message
                              : `could not create the ${terms.battle.singular}`,
                          );
                        }
                      },
                    },
                  );
                }}
              >
                {create.isPending
                  ? "Creating…"
                  : `Create for ${formatAmountWithUnit(priceCents, currency)}`}
              </Button>
              <Link to="/battles" className={buttonVariants({ variant: "ghost", size: "sm" })}>
                Cancel
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function SwitchRow({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="grid">
        <Label htmlFor={id}>{label}</Label>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
